import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Grid, Chip,
  Alert, CircularProgress, MenuItem, Divider, Tooltip, Tabs, Tab,
  Switch, FormControlLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton
} from '@mui/material';
import WaterIcon from '@mui/icons-material/Water';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

const fmt = (v) => {
  const n = parseFloat(v || 0);
  const abs = Math.abs(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `(${abs})` : abs;
};
const fmtNum = (v) => parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const COLOR_OPE = '#1565c0';
const COLOR_INV = '#2e7d32';
const COLOR_FIN = '#6a1b9a';
const TIPO_LABELS = { OPE: 'ACTIVIDADES DE OPERACIÓN', INV: 'ACTIVIDADES DE INVERSIÓN', FIN: 'ACTIVIDADES DE FINANCIAMIENTO' };
const TIPO_COLORS = { OPE: COLOR_OPE, INV: COLOR_INV, FIN: COLOR_FIN };

function VariacionChip({ v1, v2 }) {
  if (v2 === null || v2 === undefined) return null;
  const diff = v1 - v2;
  const pct = v2 !== 0 ? ((diff / Math.abs(v2)) * 100).toFixed(1) : null;
  const color = diff >= 0 ? 'success' : 'error';
  const icon = diff > 0 ? <TrendingUpIcon sx={{ fontSize: 12 }} />
    : diff < 0 ? <TrendingDownIcon sx={{ fontSize: 12 }} />
    : <TrendingFlatIcon sx={{ fontSize: 12 }} />;
  return (
    <Chip icon={icon} label={pct !== null ? `${diff >= 0 ? '+' : ''}${pct}%` : (diff >= 0 ? '+' : '') + fmt(diff)}
      size="small" color={color} variant="outlined"
      sx={{ fontSize: 9, height: 18, '& .MuiChip-icon': { ml: '4px' } }} />
  );
}

// ─── SECCIÓN EFE DIRECTO ────────────────────────────────────────────────────
function SeccionEFE({ tipo, movimientos, catalogo, movimientos2 }) {
  const items = movimientos.filter(m => m.tipo_actividad === tipo);
  const items2 = movimientos2?.filter(m => m.tipo_actividad === tipo) || [];
  const mapa2 = {};
  for (const m of items2) mapa2[m.urc_codigo] = m.flujo_neto;
  const catalogoTipo = catalogo.filter(c => c.tipo_actividad === tipo);
  const mapaFlujo = {};
  for (const m of items) mapaFlujo[m.urc_codigo] = m.flujo_neto;
  const subtotal = items.reduce((s, m) => s + m.flujo_neto, 0);
  const subtotal2 = items2.reduce((s, m) => s + m.flujo_neto, 0);
  const color = TIPO_COLORS[tipo];
  const hasComparative = movimientos2 !== null && movimientos2 !== undefined;

  return (
    <>
      <TableRow>
        <TableCell colSpan={hasComparative ? 5 : 4} sx={{
          bgcolor: color, color: 'white', fontWeight: 'bold', fontSize: 12, py: 0.7, letterSpacing: 1
        }}>
          {TIPO_LABELS[tipo]}
        </TableCell>
      </TableRow>
      {catalogoTipo.map(u => {
        const flujo = mapaFlujo[u.codigo] ?? null;
        const flujo2 = mapa2[u.codigo] ?? null;
        const sinDatos = flujo === null;
        return (
          <TableRow key={u.codigo} sx={{ '&:hover': { bgcolor: '#f5f5f5' }, opacity: sinDatos ? 0.45 : 1 }}>
            <TableCell sx={{ pl: 4, py: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={u.codigo} size="small" variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: 10, height: 18, color, borderColor: color, minWidth: 32 }} />
                <Typography variant="body2" sx={{ fontSize: 12 }}>{u.descripcion}</Typography>
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.5, color: sinDatos ? 'text.disabled' : 'inherit' }}>
              {sinDatos ? '—' : fmt(flujo)}
            </TableCell>
            {hasComparative && (
              <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.5, color: flujo2 === null ? 'text.disabled' : 'text.secondary' }}>
                {flujo2 === null ? '—' : fmt(flujo2)}
              </TableCell>
            )}
            {hasComparative && (
              <TableCell align="center" sx={{ py: 0.5 }}>
                {flujo !== null && flujo2 !== null && <VariacionChip v1={flujo} v2={flujo2} />}
              </TableCell>
            )}
          </TableRow>
        );
      })}
      <TableRow sx={{ bgcolor: `${color}18` }}>
        <TableCell sx={{ pl: 4, py: 0.7, fontWeight: 'bold', fontSize: 12, color }}>
          FLUJO NETO — {tipo === 'OPE' ? 'OPERACIÓN' : tipo === 'INV' ? 'INVERSIÓN' : 'FINANCIAMIENTO'}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: 13, py: 0.7, color: subtotal >= 0 ? '#2e7d32' : '#c62828' }}>
          {fmt(subtotal)}
        </TableCell>
        {hasComparative && (
          <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, py: 0.7, color: subtotal2 >= 0 ? '#2e7d32' : '#c62828', opacity: 0.75 }}>
            {fmt(subtotal2)}
          </TableCell>
        )}
        {hasComparative && (
          <TableCell align="center" sx={{ py: 0.7 }}>
            <VariacionChip v1={subtotal} v2={subtotal2} />
          </TableCell>
        )}
      </TableRow>
    </>
  );
}

// ─── FILA EFE INDIRECTO ────────────────────────────────────────────────────
function FilaIndirecto({ label, v1, v2, bold, indent = 0, bgColor, textColor, dividerTop }) {
  return (
    <>
      {dividerTop && <TableRow><TableCell colSpan={4} sx={{ p: 0, borderBottom: '2px solid #90caf9' }} /></TableRow>}
      <TableRow sx={{ bgcolor: bgColor || 'inherit', '&:hover': { bgcolor: '#f5f5f5' } }}>
        <TableCell sx={{ pl: 2 + indent * 2, py: 0.5, fontWeight: bold ? 'bold' : 'normal', fontSize: 12, color: textColor || 'inherit' }}>
          {label}
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: bold ? 'bold' : 'normal', fontSize: bold ? 13 : 12, py: 0.5, color: textColor || (v1 >= 0 ? 'inherit' : '#c62828') }}>
          {fmt(v1)}
        </TableCell>
        {v2 !== undefined && (
          <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, py: 0.5, color: 'text.secondary', opacity: 0.8 }}>
            {fmt(v2)}
          </TableCell>
        )}
        {v2 !== undefined && (
          <TableCell align="center" sx={{ py: 0.5 }}>
            <VariacionChip v1={v1} v2={v2} />
          </TableCell>
        )}
      </TableRow>
    </>
  );
}

// ─── REPORTE EFE INDIRECTO ──────────────────────────────────────────────────
function TabIndirecto({ empresaId, fechaIni, fechaFin, fechaIni2, fechaFin2, comparativo, empresa }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const printRef = useRef();

  const generar = useCallback(async () => {
    if (!empresaId || !fechaIni || !fechaFin) return;
    setLoading(true);
    setError('');
    try {
      const params = { empresa_id: empresaId, fecha_ini: fechaIni, fecha_fin: fechaFin };
      if (comparativo && fechaIni2 && fechaFin2) { params.fecha_ini_2 = fechaIni2; params.fecha_fin_2 = fechaFin2; }
      const r = await axios.get('/api/contabilidad/reportes/estado-flujos-indirecto', { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId, fechaIni, fechaFin, comparativo, fechaIni2, fechaFin2]);

  const handleImprimir = () => {
    const contenido = printRef.current;
    if (!contenido) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(`<html><head><title>EFE Método Indirecto</title>
      <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px;}
      table{border-collapse:collapse;width:100%;}th,td{padding:4px 8px;border-bottom:1px solid #ddd;}
      .monospace{font-family:monospace;}.bold{font-weight:bold;}
      @media print{body{margin:0;}}</style></head><body>${contenido.innerHTML}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => { w.print(); w.close(); }, 500);
  };

  const p = data?.periodo;
  const p2 = data?.periodo2;
  const hasComp = comparativo && p2;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={generar} disabled={loading || !empresaId || !fechaIni || !fechaFin}>
          {loading ? 'Calculando...' : 'Generar Método Indirecto'}
        </Button>
        {data && (
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handleImprimir}>
            Imprimir
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!data && !loading && (
        <Alert severity="info" icon={<InfoOutlinedIcon />}>
          El <strong>Método Indirecto</strong> parte de la utilidad neta del período y la ajusta por partidas que no son movimientos de efectivo
          (depreciación, amortización) y por cambios en el capital de trabajo (CxC, inventarios, CxP).
          <br />Este método se calcula <strong>automáticamente</strong> desde los saldos contables, sin requerir codificación URC.
        </Alert>
      )}

      {data && !loading && (
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          <div ref={printRef}>
            <Box sx={{ bgcolor: '#1a237e', color: 'white', p: 2, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                {empresa?.razon_social || ''}
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                ESTADO DE FLUJOS DE EFECTIVO — MÉTODO INDIRECTO
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                NIC 7 / PCGE Perú
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Del {data.fecha_ini} al {data.fecha_fin}
                {empresa?.moneda_base && ` — Expresado en ${empresa.moneda_base}`}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.200' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 12 }}>Concepto</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 12, width: 130 }}>
                      {data.fecha_fin?.slice(0, 4) || 'Período'}
                    </TableCell>
                    {hasComp && <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 12, width: 130 }}>
                      {p2.fecha_fin?.slice(0, 4) || 'Comparativo'}
                    </TableCell>}
                    {hasComp && <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: 11, width: 80 }}>Var %</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* OPERACIÓN */}
                  <TableRow><TableCell colSpan={hasComp ? 4 : 2} sx={{ bgcolor: COLOR_OPE, color: 'white', fontWeight: 'bold', fontSize: 12, py: 0.7, letterSpacing: 1 }}>
                    ACTIVIDADES DE OPERACIÓN
                  </TableCell></TableRow>
                  <FilaIndirecto label="Utilidad (Pérdida) Neta del Período" v1={p.utilidad_neta} v2={hasComp ? p2.utilidad_neta : undefined} bold indent={1} />
                  <TableRow><TableCell colSpan={hasComp ? 4 : 2} sx={{ pl: 3, py: 0.3, fontSize: 11, color: 'text.secondary', fontStyle: 'italic', bgcolor: '#f5f5f5' }}>
                    Ajustes por partidas que no afectan el efectivo:
                  </TableCell></TableRow>
                  <FilaIndirecto label="(+) Depreciación y amortización (cta. 68)" v1={p.depreciacion} v2={hasComp ? p2.depreciacion : undefined} indent={2} />
                  <TableRow><TableCell colSpan={hasComp ? 4 : 2} sx={{ pl: 3, py: 0.3, fontSize: 11, color: 'text.secondary', fontStyle: 'italic', bgcolor: '#f5f5f5' }}>
                    Cambios en capital de trabajo:
                  </TableCell></TableRow>
                  <FilaIndirecto label="(+/-) Variación en Cuentas por Cobrar (12/13/14)" v1={p.var_cxc} v2={hasComp ? p2.var_cxc : undefined} indent={2} />
                  <FilaIndirecto label="(+/-) Variación en Inventarios (20-28)" v1={p.var_inventarios} v2={hasComp ? p2.var_inventarios : undefined} indent={2} />
                  <FilaIndirecto label="(+/-) Variación en Cuentas por Pagar (41-46)" v1={p.var_cxp} v2={hasComp ? p2.var_cxp : undefined} indent={2} />
                  <FilaIndirecto label="FLUJO NETO DE ACTIVIDADES DE OPERACIÓN" v1={p.cash_operaciones} v2={hasComp ? p2.cash_operaciones : undefined}
                    bold bgColor={`${COLOR_OPE}18`} textColor={p.cash_operaciones >= 0 ? COLOR_OPE : '#c62828'} dividerTop />

                  {/* INVERSIÓN */}
                  <TableRow><TableCell colSpan={hasComp ? 4 : 2} sx={{ bgcolor: COLOR_INV, color: 'white', fontWeight: 'bold', fontSize: 12, py: 0.7, letterSpacing: 1 }}>
                    ACTIVIDADES DE INVERSIÓN
                  </TableCell></TableRow>
                  <FilaIndirecto label="(+/-) Variación en Activos Fijos (33-36)" v1={p.var_activos_fijos} v2={hasComp ? p2.var_activos_fijos : undefined} indent={1} />
                  <FilaIndirecto label="FLUJO NETO DE ACTIVIDADES DE INVERSIÓN" v1={p.cash_inversion} v2={hasComp ? p2.cash_inversion : undefined}
                    bold bgColor={`${COLOR_INV}18`} textColor={p.cash_inversion >= 0 ? COLOR_INV : '#c62828'} dividerTop />

                  {/* FINANCIAMIENTO */}
                  <TableRow><TableCell colSpan={hasComp ? 4 : 2} sx={{ bgcolor: COLOR_FIN, color: 'white', fontWeight: 'bold', fontSize: 12, py: 0.7, letterSpacing: 1 }}>
                    ACTIVIDADES DE FINANCIAMIENTO
                  </TableCell></TableRow>
                  <FilaIndirecto label="(+/-) Variación en Préstamos y Deudas (45/47/48)" v1={p.var_prestamos} v2={hasComp ? p2.var_prestamos : undefined} indent={1} />
                  <FilaIndirecto label="(+/-) Variación en Capital y Reservas (50/52)" v1={p.var_capital} v2={hasComp ? p2.var_capital : undefined} indent={1} />
                  <FilaIndirecto label="FLUJO NETO DE ACTIVIDADES DE FINANCIAMIENTO" v1={p.cash_financiamiento} v2={hasComp ? p2.cash_financiamiento : undefined}
                    bold bgColor={`${COLOR_FIN}18`} textColor={p.cash_financiamiento >= 0 ? COLOR_FIN : '#c62828'} dividerTop />

                  {/* Saldos */}
                  <TableRow sx={{ bgcolor: '#1a237e' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 13, py: 1 }}>
                      VARIACIÓN NETA DEL EFECTIVO
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 14, py: 1, color: p.variacion_neta >= 0 ? '#a5d6a7' : '#ef9a9a' }}>
                      {fmt(p.variacion_neta)}
                    </TableCell>
                    {hasComp && <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, py: 1, color: p2.variacion_neta >= 0 ? '#a5d6a7' : '#ef9a9a', opacity: 0.8 }}>
                      {fmt(p2.variacion_neta)}
                    </TableCell>}
                    {hasComp && <TableCell align="center" sx={{ py: 1 }}><VariacionChip v1={p.variacion_neta} v2={p2.variacion_neta} /></TableCell>}
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: 12, py: 0.8, pl: 2, color: '#1a237e' }}>Saldo inicial de efectivo</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 12, color: '#1a237e' }}>{fmtNum(p.saldo_inicial)}</TableCell>
                    {hasComp && <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: '#1a237e', opacity: 0.7 }}>{fmtNum(p2.saldo_inicial)}</TableCell>}
                    {hasComp && <TableCell />}
                  </TableRow>
                  <TableRow sx={{ bgcolor: '#3949ab' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14, py: 1, pl: 2 }}>SALDO FINAL DE EFECTIVO</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 15, py: 1, color: 'white' }}>{fmtNum(p.saldo_final)}</TableCell>
                    {hasComp && <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 14, py: 1, color: 'white', opacity: 0.8 }}>{fmtNum(p2.saldo_final)}</TableCell>}
                    {hasComp && <TableCell align="center" sx={{ py: 1 }}><VariacionChip v1={p.saldo_final} v2={p2.saldo_final} /></TableCell>}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Paper>
      )}
    </Box>
  );
}

// ─── COMPONENTE PRINCIPAL ────────────────────────────────────────────────────
export default function EstadoFlujosCash() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [fechaIni, setFechaIni] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [comparativo, setComparativo] = useState(false);
  const [fechaIni2, setFechaIni2] = useState('');
  const [fechaFin2, setFechaFin2] = useState('');
  const [reporte, setReporte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [dialogCaja, setDialogCaja] = useState(false);
  const [cuentasCaja, setCuentasCaja] = useState('10,12');
  const [savingCaja, setSavingCaja] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    const now = new Date();
    const anio = now.getFullYear();
    setFechaIni(`${anio}-01-01`);
    setFechaFin(`${anio}-12-31`);
    setFechaIni2(`${anio - 1}-01-01`);
    setFechaFin2(`${anio - 1}-12-31`);
  }, []);

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get('/api/contabilidad/cuentas-caja', { params: { empresa_id: empresaId } })
      .then(r => setCuentasCaja(r.data.cuentas_caja || '10,12'))
      .catch(() => setCuentasCaja('10,12'));
  }, [empresaId]);

  const generar = useCallback(async () => {
    if (!empresaId || !fechaIni || !fechaFin) return;
    setLoading(true);
    setError('');
    try {
      const params = { empresa_id: empresaId, fecha_ini: fechaIni, fecha_fin: fechaFin };
      if (comparativo && fechaIni2 && fechaFin2) {
        params.fecha_ini_2 = fechaIni2;
        params.fecha_fin_2 = fechaFin2;
      }
      const r = await axios.get('/api/contabilidad/reportes/estado-flujos', { params });
      setReporte(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId, fechaIni, fechaFin, comparativo, fechaIni2, fechaFin2]);

  const guardarCuentasCaja = async () => {
    if (!empresaId) return;
    setSavingCaja(true);
    try {
      await axios.put('/api/contabilidad/cuentas-caja', { empresa_id: empresaId, cuentas_caja: cuentasCaja });
      setDialogCaja(false);
      setReporte(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setSavingCaja(false);
    }
  };

  const handleImprimir = () => {
    const contenido = printRef.current;
    if (!contenido) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(`<html><head><title>Estado de Flujos de Efectivo</title>
      <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px;}
      table{border-collapse:collapse;width:100%;}th,td{padding:4px 8px;border-bottom:1px solid #ddd;}
      .section-header{background:#1a237e;color:white;font-weight:bold;padding:6px 8px;}
      .section-subtotal{background:#e8eaf6;font-weight:bold;}.text-right{text-align:right;}
      .monospace{font-family:monospace;}.saldo-row{background:#f5f5f5;font-weight:bold;border-top:2px solid #333;}
      @media print{body{margin:0;}}</style></head>
      <body>${contenido.innerHTML}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => { w.print(); w.close(); }, 500);
  };

  const handleExcel = () => {
    if (!reporte) return;
    const wb = XLSX.utils.book_new();
    const empresa = empresas.find(e => String(e.id) === String(empresaId));
    const movs = reporte.movimientos || [];
    const cat = reporte.catalogo || [];
    const variacionNeta = movs.reduce((s, m) => s + m.flujo_neto, 0);
    const hasComp = !!reporte.periodo2;
    const p2 = reporte.periodo2;

    const filas = [];
    const header = hasComp
      ? ['Concepto', `${reporte.fecha_ini} al ${reporte.fecha_fin}`, `${p2?.fecha_ini} al ${p2?.fecha_fin}`, 'Variación']
      : ['Concepto', `${reporte.fecha_ini} al ${reporte.fecha_fin}`];

    filas.push([empresa?.razon_social || '']);
    filas.push(['ESTADO DE FLUJOS DE EFECTIVO — MÉTODO DIRECTO']);
    filas.push([`Del ${reporte.fecha_ini} al ${reporte.fecha_fin}`]);
    filas.push([]);
    filas.push(header);

    const saldo_ini2 = p2?.saldo_inicial;
    filas.push(hasComp
      ? ['Saldo inicial de efectivo', reporte.saldo_inicial, saldo_ini2, reporte.saldo_inicial - (saldo_ini2 || 0)]
      : ['Saldo inicial de efectivo', reporte.saldo_inicial]);

    for (const tipo of ['OPE', 'INV', 'FIN']) {
      filas.push([TIPO_LABELS[tipo]]);
      const ctaTipo = cat.filter(c => c.tipo_actividad === tipo);
      const mapaFlujo = {};
      const mapaFlujo2 = {};
      for (const m of movs.filter(m => m.tipo_actividad === tipo)) mapaFlujo[m.urc_codigo] = m.flujo_neto;
      for (const m of (p2?.movimientos || []).filter(m => m.tipo_actividad === tipo)) mapaFlujo2[m.urc_codigo] = m.flujo_neto;
      for (const u of ctaTipo) {
        const v1 = mapaFlujo[u.codigo] ?? '';
        const v2 = hasComp ? (mapaFlujo2[u.codigo] ?? '') : undefined;
        filas.push(hasComp ? [`  ${u.codigo} ${u.descripcion}`, v1, v2, v1 !== '' && v2 !== '' ? v1 - v2 : ''] : [`  ${u.codigo} ${u.descripcion}`, v1]);
      }
      const sub1 = movs.filter(m => m.tipo_actividad === tipo).reduce((s, m) => s + m.flujo_neto, 0);
      const sub2 = hasComp ? (p2?.movimientos || []).filter(m => m.tipo_actividad === tipo).reduce((s, m) => s + m.flujo_neto, 0) : undefined;
      filas.push(hasComp ? [`Flujo Neto ${tipo}`, sub1, sub2, sub1 - (sub2 || 0)] : [`Flujo Neto ${tipo}`, sub1]);
      filas.push([]);
    }

    if (Math.abs(reporte.flujo_sin_clasificar) > 0.01) {
      filas.push(['⚠ Sin clasificar (sin URC)', reporte.flujo_sin_clasificar]);
    }
    filas.push(hasComp
      ? ['VARIACIÓN NETA DEL EFECTIVO', variacionNeta, (p2?.movimientos || []).reduce((s, m) => s + m.flujo_neto, 0), '']
      : ['VARIACIÓN NETA DEL EFECTIVO', variacionNeta]);
    filas.push(hasComp
      ? ['SALDO FINAL DE EFECTIVO', reporte.saldo_final, p2?.saldo_final, reporte.saldo_final - (p2?.saldo_final || 0)]
      : ['SALDO FINAL DE EFECTIVO', reporte.saldo_final]);

    const ws = XLSX.utils.aoa_to_sheet(filas);
    ws['!cols'] = [{ wch: 55 }, { wch: 18 }, ...(hasComp ? [{ wch: 18 }, { wch: 14 }] : [])];
    XLSX.utils.book_append_sheet(wb, ws, 'EFE Directo');

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `EFE_${empresa?.razon_social?.replace(/\s/g, '_') || 'empresa'}_${reporte.fecha_ini}_${reporte.fecha_fin}.xlsx`);
  };

  const empresa = empresas.find(e => String(e.id) === String(empresaId));
  const movs = reporte?.movimientos || [];
  const cat = reporte?.catalogo || [];
  const variacionNeta = movs.reduce((s, m) => s + m.flujo_neto, 0);
  const movs2 = reporte?.periodo2?.movimientos || null;
  const hasComp = comparativo && !!reporte?.periodo2;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, width: '100%' }}>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <WaterIcon sx={{ fontSize: 32, color: '#1565c0' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Estado de Flujos de Efectivo</Typography>
            <Typography variant="body2" color="text.secondary">NIC 7 / PCGE Perú</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { sm: 'auto' } }}>
          {empresaId && (
            <Tooltip title="Configurar cuentas de caja/efectivo">
              <Chip icon={<SettingsIcon sx={{ fontSize: 14 }} />}
                label={`Cuentas caja: ${cuentasCaja}`}
                size="small" variant="outlined" onClick={() => setDialogCaja(true)}
                sx={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
          <TextField select label="Empresa" size="small" value={empresaId}
            onChange={e => { setEmpresaId(e.target.value); setReporte(null); }}
            sx={{ minWidth: 260 }}>
            {empresas.map(e => (
              <MenuItem key={e.id} value={String(e.id)}>
                {e.razon_social}{!e.activo && <span style={{ color: '#999', marginLeft: 6 }}>(Inactiva)</span>}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Método Directo" />
        <Tab label="Método Indirecto" />
      </Tabs>

      {/* Filtros comunes */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField label="Fecha inicio" type="date" size="small" fullWidth
              value={fechaIni} InputLabelProps={{ shrink: true }}
              onChange={e => { setFechaIni(e.target.value); setReporte(null); }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Fecha fin" type="date" size="small" fullWidth
              value={fechaFin} InputLabelProps={{ shrink: true }}
              onChange={e => { setFechaFin(e.target.value); setReporte(null); }} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Switch checked={comparativo} onChange={e => { setComparativo(e.target.checked); setReporte(null); }} size="small" />}
              label={<Typography variant="body2">Comparativo</Typography>}
            />
          </Grid>
          {tab === 0 && (
            <Grid item xs={12} sm={3}>
              <Button fullWidth variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={generar} disabled={loading || !empresaId || !fechaIni || !fechaFin}>
                {loading ? 'Generando...' : 'Generar Reporte'}
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Segunda fecha comparativa */}
        {comparativo && (
          <Grid container spacing={2} sx={{ mt: 0.5 }} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="caption" color="secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CompareArrowsIcon sx={{ fontSize: 14 }} /> Período comparativo:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Fecha inicio (comp.)" type="date" size="small" fullWidth
                value={fechaIni2} InputLabelProps={{ shrink: true }}
                onChange={e => { setFechaIni2(e.target.value); setReporte(null); }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Fecha fin (comp.)" type="date" size="small" fullWidth
                value={fechaFin2} InputLabelProps={{ shrink: true }}
                onChange={e => { setFechaFin2(e.target.value); setReporte(null); }} />
            </Grid>
          </Grid>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── TAB DIRECTO ── */}
      {tab === 0 && (
        <>
          {!reporte && !loading && (
            <Alert severity="info" icon={<InfoOutlinedIcon />}>
              Seleccione empresa y rango de fechas, luego haga clic en <strong>Generar Reporte</strong>.
              Para que aparezcan datos, asigne códigos <strong>URC</strong> a las líneas de asiento que
              afecten cuentas de efectivo — use el módulo <em>Clasificación URC</em> para clasificar retroactivamente.
            </Alert>
          )}

          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>}

          {reporte && !loading && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
                {reporte.lineas_sin_urc > 0 && (
                  <Chip label={`${reporte.lineas_sin_urc} líneas sin URC`} color="warning" size="small" />
                )}
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleExcel}>
                  Excel
                </Button>
                <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handleImprimir}>
                  Imprimir
                </Button>
              </Box>

              <Paper elevation={3} sx={{ overflow: 'hidden' }}>
                <div ref={printRef}>
                  <Box sx={{ bgcolor: '#1a237e', color: 'white', p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                      {empresa?.razon_social || ''}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      ESTADO DE FLUJOS DE EFECTIVO
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      Método Directo — NIC 7 / PCGE Perú
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      Del {reporte.fecha_ini} al {reporte.fecha_fin}
                      {empresa?.moneda_base && ` — Expresado en ${empresa.moneda_base}`}
                    </Typography>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.200' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: 12 }}>Concepto</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 12, width: 130 }}>
                            {reporte.fecha_fin?.slice(0, 4) || 'Período'}
                          </TableCell>
                          {hasComp && <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 12, width: 130 }}>
                            {reporte.periodo2?.fecha_fin?.slice(0, 4) || 'Comparativo'}
                          </TableCell>}
                          {hasComp && <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: 11, width: 80 }}>Var %</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Saldo inicial */}
                        <TableRow sx={{ bgcolor: '#f0f4ff' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: 12, py: 0.8, pl: 2 }}>
                            Saldo inicial de efectivo y equivalentes
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 13 }}>
                            {fmtNum(reporte.saldo_inicial)}
                          </TableCell>
                          {hasComp && <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                            {fmtNum(reporte.periodo2.saldo_inicial)}
                          </TableCell>}
                          {hasComp && <TableCell align="center">
                            <VariacionChip v1={reporte.saldo_inicial} v2={reporte.periodo2.saldo_inicial} />
                          </TableCell>}
                        </TableRow>

                        <SeccionEFE tipo="OPE" movimientos={movs} catalogo={cat} movimientos2={hasComp ? movs2 : null} />
                        <SeccionEFE tipo="INV" movimientos={movs} catalogo={cat} movimientos2={hasComp ? movs2 : null} />
                        <SeccionEFE tipo="FIN" movimientos={movs} catalogo={cat} movimientos2={hasComp ? movs2 : null} />

                        {/* Sin clasificar */}
                        {Math.abs(reporte.flujo_sin_clasificar) > 0.01 && (
                          <>
                            <TableRow>
                              <TableCell colSpan={hasComp ? 4 : 2} sx={{ bgcolor: '#fff3e0', fontWeight: 'bold', fontSize: 11, py: 0.5, color: '#e65100' }}>
                                ⚠ MOVIMIENTOS SIN CLASIFICAR — use "Clasificación URC" para asignar códigos
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ pl: 4, fontSize: 12, color: '#e65100' }}>
                                Flujos no clasificados en cuentas de efectivo
                              </TableCell>
                              <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: '#e65100' }}>
                                {fmt(reporte.flujo_sin_clasificar)}
                              </TableCell>
                              {hasComp && <TableCell />}
                              {hasComp && <TableCell />}
                            </TableRow>
                          </>
                        )}

                        {/* Variación neta */}
                        <TableRow sx={{ bgcolor: '#1a237e' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 13, py: 1 }}>
                            VARIACIÓN NETA DEL EFECTIVO Y EQUIVALENTES
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 14, py: 1, color: variacionNeta >= 0 ? '#a5d6a7' : '#ef9a9a' }}>
                            {fmt(variacionNeta)}
                          </TableCell>
                          {hasComp && (() => {
                            const vn2 = (movs2 || []).reduce((s, m) => s + m.flujo_neto, 0);
                            return (<><TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 13, py: 1, color: vn2 >= 0 ? '#a5d6a7' : '#ef9a9a', opacity: 0.8 }}>{fmt(vn2)}</TableCell>
                              <TableCell align="center"><VariacionChip v1={variacionNeta} v2={vn2} /></TableCell></>);
                          })()}
                        </TableRow>

                        {/* Saldo final */}
                        <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: 12, py: 0.8, pl: 2, color: '#1a237e' }}>Saldo inicial de efectivo</TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 12, color: '#1a237e' }}>{fmtNum(reporte.saldo_inicial)}</TableCell>
                          {hasComp && <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 12, color: '#1a237e', opacity: 0.7 }}>{fmtNum(reporte.periodo2.saldo_inicial)}</TableCell>}
                          {hasComp && <TableCell />}
                        </TableRow>
                        <TableRow sx={{ bgcolor: '#3949ab' }}>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14, py: 1, pl: 2 }}>
                            SALDO FINAL DE EFECTIVO Y EQUIVALENTES
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: 15, py: 1, color: 'white' }}>
                            {fmtNum(reporte.saldo_final)}
                          </TableCell>
                          {hasComp && <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: 14, py: 1, color: 'white', opacity: 0.8 }}>
                            {fmtNum(reporte.periodo2.saldo_final)}
                          </TableCell>}
                          {hasComp && <TableCell align="center">
                            <VariacionChip v1={reporte.saldo_final} v2={reporte.periodo2.saldo_final} />
                          </TableCell>}
                        </TableRow>

                        {/* Verificación */}
                        <TableRow>
                          <TableCell colSpan={hasComp ? 4 : 2} sx={{ py: 0.3 }} />
                        </TableRow>
                        {Math.abs((reporte.saldo_inicial + variacionNeta) - reporte.saldo_final) > 0.05 && (
                          <TableRow>
                            <TableCell colSpan={hasComp ? 4 : 2} sx={{ py: 0.5 }}>
                              <Alert severity="warning" sx={{ py: 0 }}>
                                Diferencia de cuadre: {fmt((reporte.saldo_inicial + variacionNeta) - reporte.saldo_final)}.
                                Puede deberse a movimientos sin URC. Use "Clasificación URC" para resolverlo.
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </Paper>

              {/* Leyenda URC */}
              <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
                  Clasificación URC — Referencia rápida
                </Typography>
                <Grid container spacing={1}>
                  {['OPE', 'INV', 'FIN'].map(tipo => (
                    <Grid item xs={12} md={4} key={tipo}>
                      <Typography variant="caption" fontWeight="bold"
                        sx={{ color: TIPO_COLORS[tipo], display: 'block', mb: 0.5 }}>
                        {TIPO_LABELS[tipo]}
                      </Typography>
                      {cat.filter(c => c.tipo_actividad === tipo).map(u => (
                        <Box key={u.codigo} sx={{ display: 'flex', gap: 1, mb: 0.3 }}>
                          <Typography variant="caption" fontFamily="monospace" fontWeight="bold"
                            sx={{ color: TIPO_COLORS[tipo], minWidth: 22 }}>{u.codigo}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                            {u.descripcion}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </>
          )}
        </>
      )}

      {/* ── TAB INDIRECTO ── */}
      {tab === 1 && (
        <TabIndirecto
          empresaId={empresaId} fechaIni={fechaIni} fechaFin={fechaFin}
          fechaIni2={fechaIni2} fechaFin2={fechaFin2}
          comparativo={comparativo} empresa={empresa}
        />
      )}

      {/* ── DIALOG CONFIGURAR CUENTAS CAJA ── */}
      <Dialog open={dialogCaja} onClose={() => setDialogCaja(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar cuentas de efectivo (caja)</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ingrese los prefijos de cuenta separados por coma. Ejemplo: <strong>10,12</strong> incluirá todas
            las cuentas que inicien con 10 (Caja y Bancos) y 12 (Cuentas por Cobrar Comerciales - Terceros).
          </Alert>
          <TextField
            label="Prefijos de cuentas de caja (separados por coma)"
            fullWidth size="small"
            value={cuentasCaja}
            onChange={e => setCuentasCaja(e.target.value)}
            placeholder="10,12"
            helperText="PCGE Perú: cuenta 10 = Efectivo y equivalentes; cuenta 12 = Cuentas por cobrar comerciales (si se considera cuasi-caja)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogCaja(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarCuentasCaja} disabled={savingCaja}>
            {savingCaja ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
